import { Radio, RadioGroup, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { dateTimeStringFormat } from '../../../common/date/datetime-string-format';
import { useStores } from '../../../common/hooks';
import { blankEventAttendee, EventAttendee, WinnerEvent } from '../../../models';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import './EventAttendeesSection.scss';

const EventAttendeesSectionImpl: React.FC = () => {
  const {
    reviewQuestionnairePageUiStore,
  } = useStores();

  const renderEventAttendeeFields = (isFoodEvent: boolean, eventAttendee: EventAttendee) => {
    return (
      <FlexBox
        className='event-attendee-fields'
      >
        <FlexBox direction='column'>
          <CommonLabelAndValueList
            labelId='prefix'
            label='Prefix:'
            childrenFallback={eventAttendee.prefix}
          />
          <CommonLabelAndValueList
            labelId='firstName'
            label='First name:'
            childrenFallback={eventAttendee.firstName}
          />
          <CommonLabelAndValueList
            labelId='lastName'
            label='Last name:'
            childrenFallback={eventAttendee.lastName}
          />
          <CommonLabelAndValueList
            labelId='title'
            label='Title:'
            childrenFallback={eventAttendee.title}
          />
          <CommonLabelAndValueList
            labelId='email'
            label='Email:'
            childrenFallback={eventAttendee.emailAddress}
          />
        </FlexBox>
        {
          isFoodEvent &&
          <FlexBox direction='column'>
            <CommonLabelAndValueList
              labelId='primaryLanguage'
              label='Primary Language:'
              childrenFallback={eventAttendee.primaryLanguage}
            />
            <CommonLabelAndValueList
              labelId='dietaryRequest'
              label='Dietary requests:'
              childrenFallback={eventAttendee.dietaryRequests}
            />
            <CommonLabelAndValueList
              labelId='accessibilityNeeds'
              label='Accessibility needs:'
              childrenFallback={eventAttendee.accessibilityNeeds}
            />
          </FlexBox>
        }
      </FlexBox>
    )
  }

  const getEventTime = (winnerEvent: WinnerEvent): string => {
    return winnerEvent.startTime && winnerEvent.endTime ?
      `${dateTimeStringFormat(`1000-01-01T${winnerEvent.startTime}`, 'p')} - ${dateTimeStringFormat(`1000-01-01T${winnerEvent.endTime}`, 'p')} PST` :
      'Time TBD';
  }

  const renderEventAttendeeForm = (winnerEvent: WinnerEvent, attendees: EventAttendee[]) => {
    const isFoodEvent = winnerEvent.eventType === 'Food';
    return (
      <div
        key={winnerEvent.eventId}
        className='event-attendees-form'
      >
        <Text as='h4' weight='semibold'>
          {`${winnerEvent.displayName} | ${winnerEvent.isTimeTBD ? 'Date and time TBD' : `${dateTimeStringFormat(winnerEvent.eventDate, 'EEEE MMMM d, yyyy')}, ${getEventTime(winnerEvent)}`}`}
        </Text>
        <p>Will you be sending an attendee to this event?</p>
        <RadioGroup>
          <Radio
            label='Yes'
            checked={attendees[0].isAttending}
            onChange={event => attendees.forEach(attendee => attendee.isAttending = event.target.checked)}
          />
          <Radio
            label='No'
            checked={!attendees[0].isAttending}
            onChange={event => attendees.forEach(attendee => attendee.isAttending = !event.target.checked)}
          />
        </RadioGroup>
        {attendees[0].isAttending && renderEventAttendeeFields(isFoodEvent, attendees[0])}
        {
          winnerEvent.eventType === 'Multi Attendee' && attendees.length > 1 &&
          attendees.map((attendee, index) => {
            if (index === 0) return undefined;
            return (
              <div
                key={index}
              >
                {renderEventAttendeeFields(isFoodEvent, attendee)}
              </div>
            );
          })
        }
      </div>
    );
  }

  return (
    <section
      className='review-questionnaire-page__section review-questionnaire-page__section--event'
    >
      <Text
        block
        weight='semibold'
        size={400}
        as='h3'
      >
        Events
      </Text>
      {
        reviewQuestionnairePageUiStore.winnerEvents.map(winnerEvent => {
          if (!reviewQuestionnairePageUiStore.questionnaire?.eventAttendees.some(attendee => attendee.eventId === winnerEvent.eventId)) {
            reviewQuestionnairePageUiStore.questionnaire?.eventAttendees.push(blankEventAttendee(winnerEvent.eventId, reviewQuestionnairePageUiStore.questionnaire.questionnaireId))
          }
          const currentEventAttendee = reviewQuestionnairePageUiStore.questionnaire?.eventAttendees.filter(attendee => attendee.eventId === winnerEvent.eventId);
          return (
            renderEventAttendeeForm(winnerEvent, currentEventAttendee ?? [])
          );
        })
      }
    </section>
  );
}

export const EventAttendeesSection = observer(EventAttendeesSectionImpl);
